/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import { Module, Node, Button, Popover, getTelemetryObject, ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';
import { IHeaderViewProps } from '@msdyn365-commerce-modules/header';
import { Customer } from '@msdyn365-commerce/retail-proxy';
import { IHeaderProps as IHeaderExtensionProps } from '../definition-extensions/header.ext.props.autogenerated';
import { ArrayExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { CartIconComponent } from '@msdyn365-commerce/components';
export interface IHeaderViewState {
    _togglePopover: boolean;
    signinPopoverOpen: boolean;
}

/**
 *
 * Header component
 * @extends {React.PureComponent<IHeaderViewProps & IHeaderExtensionProps<{}>>}
 */
export class HeaderView extends React.PureComponent<IHeaderViewProps & IHeaderExtensionProps<{}>, IHeaderViewState> {
    private popOverRef: React.RefObject<HTMLButtonElement>;
    private readonly telemetryContent: ITelemetryContent;
    constructor(props: IHeaderViewProps & IHeaderExtensionProps<{}>) {
        super(props);
        this.popOverRef = React.createRef();
        this.state = {
            _togglePopover: false,
            signinPopoverOpen: false
        };
        this.telemetryContent = getTelemetryObject(
            this.props.context.request.telemetryPageName!,
            this.props.friendlyName,
            this.props.telemetry
        );
    }
    public render(): JSX.Element {
        const {
            HeaderTag,
            HeaderContainer,
            HeaderTopBarContainer,
            MobileMenuContainer,
            MobileMenuBodyContainer,
            MobileMenuLinksContainer,
            context: {
                request: {
                    user: { signUpUrl, signOutUrl }
                }
            },
            id,
            typeName,
            config: { homePageLink, contactUsLink, signoutLinkText, registerLinkText },
            context,
            resources: { cartLabel, cartQtyLabel }
        } = this.props;
        const cartIconSlot = this._getSlot('cartIcon');
        const { navIcon, logo, search, MobileMenuHeader, menuBar, accountLinks, signInLink, signOutLink, data } = this.props;
        const isUserSignedIn = data.accountInformation.result?.AccountNumber ? true : false;
        return (
            <Module {...HeaderTag}>
                <Node {...HeaderContainer}>
                    <Node {...HeaderTopBarContainer}>
                        <div className='container'>
                            <div className='row'>
                                <div className='col-12'>{navIcon}</div>
                                <div className='col-md-4'>{logo}</div>
                                <div className='col-md-4 search-wrap'>{this.renderReactFragment(search)}</div>
                                <div className='col-md-4 custom-header-wrap'>
                                    {this.renderDesktopAccountBlock()}
                                    {ArrayExtensions.hasElements(cartIconSlot) ? (
                                        cartIconSlot[0]
                                    ) : (
                                        <CartIconComponent
                                            cartLabel={cartLabel}
                                            cartQtyLabel={cartQtyLabel}
                                            context={context}
                                            id={id}
                                            typeName={typeName}
                                            telemetryContent={this.telemetryContent}
                                            data={{ cart: this.props.data.cart && this.props.data.cart.result }}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                    </Node>
                    <Node {...MobileMenuContainer}>
                        <Node {...MobileMenuBodyContainer}>
                            <div className='nav-logo'>{logo}</div>
                            {MobileMenuHeader}
                            {this.renderReactFragment(menuBar)}
                            <Node {...MobileMenuLinksContainer}>
                                {accountLinks ? accountLinks.map(link => link) : false}
                                <div className='signIn-link'>{signInLink}</div>
                                <div className='signOut-link'>{signOutLink}</div>
                            </Node>
                            <div className='navigation-bottom-container'>
                                <ul className='navigation-mobile-bottom-list'>
                                    {homePageLink && homePageLink.linkText && (
                                        <li>
                                            <a href={homePageLink.linkUrl.destinationUrl}>{homePageLink.linkText}</a>
                                        </li>
                                    )}
                                    |
                                    {contactUsLink && contactUsLink.linkText && (
                                        <li>
                                            <a href={contactUsLink.linkUrl.destinationUrl}>{contactUsLink.linkText}</a>
                                        </li>
                                    )}
                                    |
                                    {isUserSignedIn
                                        ? signoutLinkText && (
                                              <li>
                                                  <a href={signOutUrl}>{signoutLinkText}</a>
                                              </li>
                                          )
                                        : registerLinkText && (
                                              <li>
                                                  <a href={signUpUrl}>{registerLinkText}</a>
                                              </li>
                                          )}
                                </ul>
                            </div>
                        </Node>
                    </Node>
                    {this.renderReactFragment(menuBar)}
                </Node>
            </Module>
        );
    }
    private _getSlot(slotName: string): React.ReactNode[] | null {
        const { slots } = this.props;
        return (slots && slots[slotName] && slots[slotName].length && slots[slotName]) || null;
    }
    private togglePopOver = () => {
        this.setState({
            signinPopoverOpen: !this.state.signinPopoverOpen
        });
    };
    private getUserOptions = (): React.ReactNode | undefined => {
        return (
            <Button
                innerRef={this.popOverRef}
                className='ms-header__user-options-button'
                aria-describedby='userOptionsPopover'
                onClick={this.togglePopOver}
                color='link'
                aria-expanded={this.state.signinPopoverOpen}
            />
        );
    };
    private getSignInButton = (): React.ReactNode | undefined => {
        const {
            context: {
                request: {
                    user: { signInUrl }
                }
            },
            resources: { signInLinkText, signInLinkAriaText }
        } = this.props;
        return (
            <Button className={'ms-header__sign-in-button'} href={signInUrl || ''} aria-label={signInLinkAriaText}>
                <span className='ms-header__signin-button-text' aria-hidden={false}>
                    {signInLinkText}
                </span>
            </Button>
        );
    };
    private getSignUpButton = (): React.ReactNode | undefined => {
        const {
            context: {
                request: {
                    user: { signUpUrl }
                }
            }
        } = this.props;
        return (
            <Button
                className={'ms-header__signup-button'}
                /* title={signInLinkText} */
                href={signUpUrl || ''}
                aria-label={'Create an account'}
            >
                <span className='ms-header__signup-button-text' aria-hidden={false}>
                    {'Create an account'}
                </span>
            </Button>
        );
    };
    private getDropdownPopoverConentContainer = () => {
        return {
            tag: Popover,
            id: 'userOptionsPopover',
            className: 'ms-header__user-options-content',
            placement: 'bottom',
            isOpen: this.state.signinPopoverOpen,
            target: this.popOverRef,
            toggle: this.togglePopOver
        };
    };
    private getAccountInfoDropdownButton = (customer: Customer | undefined): React.ReactNode | undefined => {
        return (
            customer &&
            customer.FirstName && (
                <Button
                    innerRef={this.popOverRef}
                    className={'ms-header__profile-button ms-header__user-options-button'}
                    aria-describedby='myprofilePopover'
                    onClick={this.togglePopOver}
                    color='link'
                    aria-expanded={this.state.signinPopoverOpen}
                />
            )
        );
    };
    private getSignInDropdownPopoverConentContainer = (customer: Customer | undefined) => {
        return (
            (customer &&
                customer.FirstName && {
                    tag: Popover,
                    id: 'myprofilePopover',
                    className: 'ms-header__account-info-content',
                    placement: 'bottom',
                    isOpen: this.state.signinPopoverOpen,
                    target: this.popOverRef,
                    toggle: this.togglePopOver
                }) ||
            undefined
        );
    };
    private renderDesktopAccountBlock = (): JSX.Element | null => {
        const {
            AccountInfoDropdownParentContainer,
            accountLinks,
            signOutLink,
            data: { accountInformation },
            config: { welcomeText, helloText }
        } = this.props;
        const customer = accountInformation && accountInformation.result;
        const accountInfoDropdownButton = this.getAccountInfoDropdownButton(customer);
        const AccountInfoDropdownPopoverConentContainer = this.getSignInDropdownPopoverConentContainer(customer);
        // following elements are used in else condition when user isn't logged in
        const userOptionsDropDownContainer = this.getDropdownPopoverConentContainer();
        const userOptionsButton = this.getUserOptions();
        const signInButton = this.getSignInButton();
        const signUpButton = this.getSignUpButton();
        if (AccountInfoDropdownParentContainer) {
            if (AccountInfoDropdownPopoverConentContainer) {
                return (
                    <Node {...AccountInfoDropdownParentContainer} onMouseEnter={this.togglePopOver} onMouseLeave={this.togglePopOver}>
                        {accountInfoDropdownButton}
                        <Node {...AccountInfoDropdownPopoverConentContainer}>
                            <div className='useroption-wrap'>
                                <div className='msc-hello-note'>
                                    {helloText && (
                                        <strong>
                                            {helloText} {customer && customer.FirstName}
                                        </strong>
                                    )}
                                </div>
                                {accountLinks
                                    ? accountLinks.map((link, index) => (
                                          <div className={`msc-account-link msc-account-link-${index}`} key={index}>
                                              {link}
                                          </div>
                                      ))
                                    : false}
                                {signOutLink}
                            </div>
                        </Node>
                    </Node>
                );
            } else if (userOptionsButton) {
                return (
                    <Node {...AccountInfoDropdownParentContainer} onMouseEnter={this.togglePopOver} onMouseLeave={this.togglePopOver}>
                        {userOptionsButton}
                        <Node {...userOptionsDropDownContainer} className='ms-header__user-options'>
                            <div className='useroption-wrap'>
                                <div className='msc-hello-note'>{welcomeText && <strong>{welcomeText}</strong>}</div>
                                {signInButton}
                                {signUpButton}
                            </div>
                        </Node>
                    </Node>
                );
            }
        }
        return null;
    };
    private renderReactFragment = (items: React.ReactNode[]): JSX.Element | null => {
        return (
            <React.Fragment>
                {items && items.length
                    ? items.map((slot: React.ReactNode, index: number) => {
                          return <React.Fragment key={index}>{slot}</React.Fragment>;
                      })
                    : null}
            </React.Fragment>
        );
    };
}

export default HeaderView;
